import { UserRoundSearch } from 'lucide-react';

import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import getCallMeBy from '@/components/user/get-call-me-by';
import HotKeyListener from '@/components/ux/hot-key-listener';
import useRedirectToInformativeDealUrl from '@/hooks/use-redirect-to-informative-deal-url';
import useUsers from '@/hooks/use-users';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import type { Customer } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import compact from 'lodash/compact';
import { useCallback, useMemo, useRef } from 'react';
import { ComboboxPopover } from '../../ui/combobox/combobox-popover';
import SalesRepBadge from './sales-rep-badge';

export const NO_SALES_REP_OPTION = {
  value: 'no-sales-rep',
  label: 'No sales rep',
  icon: <UserRoundSearch className="size-5 rounded-full" />,
};

export const transformToSalesRepOptions = (
  salesReps: NonNullable<
    Deal['primary_sales_rep'] | Deal['secondary_sales_rep']
  >[],
) => {
  return [
    NO_SALES_REP_OPTION,
    ...salesReps.map((rep) => {
      const rep_full_name =
        `${rep.first_name ?? ''} ${rep.last_name ?? ''}`.trim();
      return {
        value: rep.id,
        label: getCallMeBy(rep),
        icon: (
          <PaddockAvatar
            src={rep.avatar}
            alt={getCallMeBy(rep)}
            fallback={`${rep.first_name.charAt(0)} ${rep.last_name.charAt(0)}`}
            className="w-3.5 h-3.5 rounded-full"
          />
        ),
        searchTerms: compact([getCallMeBy(rep), rep_full_name]),
      };
    }),
  ];
};

export type SalesRepSelectorsProps = {
  dealShortId: Deal['short_id'];
  dealId: Deal['id'];
  hideCopy?: boolean;
  orientation?: 'vertical' | 'horizontal';
  side?: 'left' | 'right' | 'bottom' | 'top';
  align?: 'center' | 'start' | 'end';
  shouldRedirectToInformativeDealUrl?: boolean;
  withHotkeyListener?: boolean;
  primarySalesRep: Deal['primary_sales_rep'];
  secondarySalesRep: Deal['secondary_sales_rep'];
  vehicle: Deal['vehicle_listing'];
  primaryCustomerFullName: Customer['full_name'] | null;
  useAbbreviations?: boolean;
  className?: string;
};

export function SalesRepSelectors({
  dealId,
  dealShortId,
  hideCopy = false,
  orientation = 'vertical',
  side = 'left',
  align = 'start',
  shouldRedirectToInformativeDealUrl = false,
  withHotkeyListener = false,
  primarySalesRep,
  secondarySalesRep,
  primaryCustomerFullName,
  vehicle,
  useAbbreviations = false,
  className,
}: SalesRepSelectorsProps) {
  const salesReps = [primarySalesRep, secondarySalesRep];
  const salesRep1 = salesReps?.[0];
  const salesRep2 = salesReps?.[1];

  const { salesReps: allSalesReps } = useUsers();

  const queryClient = useQueryClient();

  const { redirectToInformativeDealUrl } = useRedirectToInformativeDealUrl();

  const updateSalesRepsMutation = useMutation({
    onMutate: async ({ salesReps }) => {
      dealStore.updateSalesReps({
        id: dealId,
        salesReps: salesReps.map((rep) => rep?.id),
      });
    },
    mutationFn: async ({
      salesReps,
      position,
    }: {
      salesReps: (Deal['primary_sales_rep'] | Deal['secondary_sales_rep'])[];
      position: number;
    }) => {
      const salesRepsIds = salesReps.map((rep) => String(rep?.id));

      const route =
        position === 0
          ? `deals/${dealId}/primary-sales-rep/${salesRepsIds[0]}`
          : `deals/${dealId}/secondary-sales-rep/${salesRepsIds[1]}`;

      const response = await pdkAxios
        .post(`/${route}`, {
          sales_rep_id: salesRepsIds[position],
        })
        .catch((e) => {
          console.error('Error updating sales reps', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      if (!vehicle) {
        return;
      }

      if (shouldRedirectToInformativeDealUrl && primaryCustomerFullName) {
        redirectToInformativeDealUrl({
          primaryCustomerFullName,
          vehicle,
          primarySalesRep,
          secondarySalesRep,
          shortId: dealShortId,
        });
      }
    },
    onError: (error, variables) => {
      console.error('Error updating sales reps', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const deletePrimarySalesRepsMutation = useMutation({
    onMutate: async ({ salesReps }) => {
      dealStore.updateSalesReps({
        id: dealId,
        salesReps: [null, salesReps[1]?.id],
      });
    },
    mutationFn: async ({
      salesReps,
    }: {
      salesReps: (Deal['primary_sales_rep'] | Deal['secondary_sales_rep'])[];
    }) => {
      const route = `deals/${dealId}/primary-sales-rep/${salesReps[0]?.id}/delete`;
      // /deals/:id/primary-sales-rep/:user_id/delete

      const response = await pdkAxios
        .post(`/${route}`, {
          sales_rep_id: salesReps[0]?.id,
        })
        .catch((e) => {
          console.error('Error deleting primary sales rep', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      if (!vehicle || !primaryCustomerFullName) {
        return;
      }

      redirectToInformativeDealUrl({
        primaryCustomerFullName,
        vehicle,
        primarySalesRep,
        secondarySalesRep,
        shortId: dealShortId,
      });
    },
    onError: (error, variables) => {
      console.error('Error deleting primary sales rep', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const deleteSecondarySalesRepsMutation = useMutation({
    onMutate: async ({ salesReps }) => {
      dealStore.updateSalesReps({
        id: dealId,
        salesReps: [salesReps[0]?.id, null],
      });
    },
    mutationFn: async ({
      salesReps,
    }: {
      salesReps: (Deal['primary_sales_rep'] | Deal['secondary_sales_rep'])[];
    }) => {
      const route = `deals/${dealId}/secondary-sales-rep/${salesReps[1]?.id}/delete`;
      // /deals/:id/secondary-sales-rep/:user_id/delete

      const response = await pdkAxios
        .post(`/${route}`, {
          sales_rep_id: salesReps[1]?.id,
        })
        .catch((e) => {
          console.error('Error deleting secondary sales rep', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {
      if (!vehicle || !primaryCustomerFullName) {
        return;
      }

      redirectToInformativeDealUrl({
        primaryCustomerFullName,
        vehicle,
        primarySalesRep,
        secondarySalesRep,
        shortId: dealShortId,
      });
    },
    onError: (error, variables) => {
      console.error('Error deleting secondary sales rep', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const salesRepOptions = useMemo(() => {
    return transformToSalesRepOptions(allSalesReps);
  }, [allSalesReps, salesRep1, salesRep2]);

  const handleSalesRepChange = useCallback(
    (position: number) => (newSalesRepId: string) => {
      if (salesRep1 && newSalesRepId === salesRep1?.id) return;
      if (salesRep2 && newSalesRepId === salesRep2?.id) return;

      if (newSalesRepId === 'no-sales-rep') {
        if (position === 0) {
          deletePrimarySalesRepsMutation.mutate({
            salesReps: [salesRep1, salesRep2],
          });
        } else {
          deleteSecondarySalesRepsMutation.mutate({
            salesReps: [salesRep1, salesRep2],
          });
        }
        return;
      }

      const newSalesReps = [...salesReps];

      const newSalesRep = allSalesReps.find(
        (option) => option.id === newSalesRepId,
      );

      if (!newSalesRep) return;

      newSalesReps[position] = newSalesRep;

      const newSalesRepsIdUnique = uniqBy(newSalesReps, 'id');
      updateSalesRepsMutation.mutate({
        salesReps: newSalesRepsIdUnique,
        position,
      });
    },
    [salesReps, salesRep1, salesRep2],
  );

  const salesRep1Ref = useRef<HTMLDivElement>(null);
  const salesRep2Ref = useRef<HTMLDivElement>(null);

  const currentSalesRep1Option = useMemo(() => {
    return salesRepOptions.find(
      (option) => option.value === (salesRep1?.id ?? 'no-sales-rep'),
    );
  }, [salesRep1, salesRepOptions]);

  const currentSalesRep2Option = useMemo(() => {
    return salesRepOptions.find(
      (option) => option.value === (salesRep2?.id ?? 'no-sales-rep'),
    );
  }, [salesRep2, salesRepOptions]);

  return (
    <div
      className={cn(
        'flex shrink-0',
        {
          'flex-col gap-1': orientation === 'vertical',
          'flex-row gap-0': orientation === 'horizontal',
        },
        className,
      )}
    >
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>{HOTKEYS.DEAL_SALES_REP_1}</KeyboardKeyIcon>
        }
        placeholder="Add sales rep..."
        onChange={handleSalesRepChange(0)}
        emptyMessage="No sales reps found"
        currentOption={currentSalesRep1Option}
        options={salesRepOptions}
        side={side}
        align={align}
      >
        <div ref={salesRep1Ref}>
          <SalesRepBadge
            salesRep={salesRep1}
            hideCopy={hideCopy}
            useAbbreviations={useAbbreviations}
          />
          {withHotkeyListener && (
            <HotKeyListener
              hotkeys={HOTKEYS.DEAL_SALES_REP_1}
              onHotkeyPressed={() => salesRep1Ref.current?.click()}
            />
          )}
        </div>
      </ComboboxPopover>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>{HOTKEYS.DEAL_SALES_REP_2}</KeyboardKeyIcon>
        }
        placeholder="Set sales rep..."
        onChange={handleSalesRepChange(1)}
        emptyMessage="No sales reps found"
        currentOption={currentSalesRep2Option}
        options={salesRepOptions}
        side={side}
        align={align}
      >
        <div ref={salesRep2Ref}>
          <SalesRepBadge
            salesRep={salesRep2}
            hideCopy={hideCopy}
            useAbbreviations={useAbbreviations}
          />
          {withHotkeyListener && (
            <HotKeyListener
              hotkeys={HOTKEYS.DEAL_SALES_REP_2}
              onHotkeyPressed={() => salesRep2Ref.current?.click()}
            />
          )}
        </div>
      </ComboboxPopover>
    </div>
  );
}
